<template>
  <div class="page-visits box grow scrollable only-y">
    <div class="visit-top-nav-shell">
      <el-card class="visit-top-nav-card" shadow="never">
        <div class="visit-top-nav">
          <h2 class="visit-page-title">{{ $t('left-menu.visits') }}</h2>

          <div class="visit-tab-switch card-base card-shadow--small">
            <button
              type="button"
              class="visit-tab-switch__item"
              :class="{ 'visit-tab-switch__item--active': activeVisitTab === 'planned' }"
              @click="activeVisitTab = 'planned'">
              <i class="mdi mdi-calendar-clock-outline visit-tab-switch__icon"></i>
              <span>{{ $t('clients.visitsList.plannedSectionTitle') }}</span>
            </button>
            <button
              type="button"
              class="visit-tab-switch__item"
              :class="{ 'visit-tab-switch__item--active': activeVisitTab === 'all' }"
              @click="activeVisitTab = 'all'">
              <i class="mdi mdi-clipboard-text-outline visit-tab-switch__icon"></i>
              <span>{{ $t('clients.visitsList.allReportsTabTitle') }}</span>
            </button>
          </div>
        </div>
      </el-card>
    </div>

    <el-tabs v-model="activeVisitTab" class="visit-tabs visit-tabs--hidden-header">
      <el-tab-pane name="planned" :label="$t('clients.visitsList.plannedSectionTitle')">
        <div class="visit-section card-base card-shadow--small mb-15">
          <div class="visit-section__header">
            <h4 class="visit-section__title">{{ $t('clients.visitsList.plannedSectionTitle') }}</h4>
            <div class="visit-section__header-actions">
              <el-button size="small" type="primary" icon="el-icon-plus" @click="openPlanningVisit">
                {{ $t('clients.visitsList.planVisit') }}
              </el-button>
            </div>
          </div>

          <div class="visit-section__filters mb-10">
            <el-radio-group v-model="plannedPeriodFilter" size="small">
              <el-radio-button label="WEEK">{{ $t('clients.visitsList.filters.thisWeek') }}</el-radio-button>
              <el-radio-button label="MONTH">{{ $t('clients.visitsList.filters.thisMonth') }}</el-radio-button>
              <el-radio-button label="FUTURE">{{ $t('clients.visitsList.filters.futurePlanned') }}</el-radio-button>
            </el-radio-group>
            <el-select
              v-if="canSelectRepresentative"
              v-model="selectedRepresentativeId"
              size="small"
              :clearable="canSelectRepresentative"
              :disabled="!canSelectRepresentative"
              filterable
              class="visits-representative-select"
              :placeholder="$t('clients.visitsList.allRepresentatives')">
              <el-option
                v-for="representative in representativeOptions"
                :key="`planned-rep-${representative.id}`"
                :label="representative.label"
                :value="representative.id">
              </el-option>
            </el-select>
          </div>

          <div v-if="showRepresentativeDebug" class="visit-rep-debug-banner">
            {{ $t('clients.visitsList.repDebugBanner', {
              selectedRepresentativeId: selectedRepresentativeId || '-',
              effectiveRepresentativeId: effectiveRepresentativeId || '-',
              optionsCount: representativeOptions.length,
            }) }}
          </div>

          <div class="planned-kpis">
            <div class="planned-kpi-card card-base card-shadow--small planned-kpi-card--planned">
              <div class="planned-kpi-card__icon-wrap">
                <i class="mdi mdi-calendar-clock-outline planned-kpi-card__icon"></i>
              </div>
              <div class="planned-kpi-card__content planned-kpi-card__content--planned">
                <div class="planned-kpi-card__top">
                  <div>
                    <div class="planned-kpi-card__value">{{ plannedSectionKpis.plannedCount }}</div>
                    <div class="planned-kpi-card__label">{{ $t('clients.visitsList.plannedKpiLabel') }}</div>
                    <div class="planned-kpi-card__hint">{{ $t('clients.visitsList.activeFilterHint') }}</div>
                  </div>
                  <div class="planned-kpi-card__pie-wrap">
                    <div class="planned-kpi-card__pie" :style="{ background: plannedTypePieGradient }">
                      <div class="planned-kpi-card__pie-hole">
                        <span>{{ plannedSectionKpis.plannedCount }}</span>
                        <small>{{ $t('clients.visitsList.visitsCountLabel') }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="planned-kpi-card__legend">
                  <div
                    v-for="typeKpi in plannedTypeChartSegments"
                    :key="typeKpi.code"
                    class="planned-kpi-card__legend-item">
                    <span class="planned-kpi-card__legend-dot" :style="{ backgroundColor: typeKpi.color }"></span>
                    <span class="planned-kpi-card__legend-label">{{ typeKpi.label }}</span>
                    <span class="planned-kpi-card__legend-value">{{ typeKpi.percentage }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="planned-kpi-card card-base card-shadow--small planned-kpi-card--reported">
              <div class="planned-kpi-card__icon-wrap">
                <i class="mdi mdi-clipboard-check-outline planned-kpi-card__icon"></i>
              </div>
              <div class="planned-kpi-card__content">
                <div class="planned-kpi-card__value">{{ plannedSectionKpis.reportedCount }}</div>
                <div class="planned-kpi-card__label">{{ $t('clients.visitsList.savedReportsLabel') }}</div>
                <div class="planned-kpi-card__hint">{{ $t('clients.visitsList.activeFilterHint') }}</div>
              </div>
            </div>

            <div class="planned-kpi-card card-base card-shadow--small planned-kpi-card--rate">
              <div class="planned-kpi-card__icon-wrap">
                <i class="mdi mdi-chart-line planned-kpi-card__icon"></i>
              </div>
              <div class="planned-kpi-card__content">
                <div class="planned-kpi-card__value">{{ plannedSectionKpis.completionRate }}%</div>
                <div class="planned-kpi-card__label">{{ $t('clients.visitsList.coverageRateLabel') }}</div>
                <div class="planned-kpi-card__hint">{{ $t('clients.visitsList.reportsOverPlannedHint') }}</div>
              </div>
            </div>
          </div>

          <div class="planned-layout">
            <div class="planned-layout__table">
              <div v-if="!loading && plannedCustomersRows.length === 0" class="secondary-text fs-13">
                {{ $t('clients.visitsList.noPlannedVisits') }}
              </div>

              <el-table
                v-else
                v-loading="loading"
                :data="plannedCustomersRows"
                size="mini"
                stripe>
                <el-table-column :label="$t('clients.name')" min-width="220">
                  <template slot-scope="scope">
                    <portaus-link
                      v-if="scope.row.customerId != null"
                      class="visit-customer-link"
                      :path="'/customers/'"
                      :id="scope.row.customerId"
                      :label="scope.row.customerLabel"
                      :underline="false"
                      fs="fs-13" />
                    <span v-else>{{ scope.row.customerLabel }}</span>
                    <div v-if="showRepresentativeDebug" class="visit-rep-debug">
                      {{ getRepresentativeDebugInfo(scope.row.plannedVisitSource || scope.row.plannedVisit || scope.row) }}
                    </div>
                  </template>
                </el-table-column>

                <el-table-column :label="$t('clients.nextVisitDate')" min-width="130">
                  <template slot-scope="scope">
                    <span>{{ formatDate(scope.row.nextVisitDate) }}</span>
                  </template>
                </el-table-column>

                <el-table-column :label="$t('clients.type')" min-width="90" align="center">
                  <template slot-scope="scope">
                    <el-tooltip :content="visitTypeLabel(getVisitTypeCode(scope.row.plannedVisitSource || scope.row.plannedVisit || scope.row))" placement="top">
                      <i :class="getVisitTypeIcon(getVisitTypeCode(scope.row.plannedVisitSource || scope.row.plannedVisit || scope.row))"></i>
                    </el-tooltip>
                  </template>
                </el-table-column>

                <el-table-column :label="$t('clients.visitsList.reportCreatedOn')" min-width="140">
                  <template slot-scope="scope">
                    <span class="planned-report-date-wrap">
                      <i v-if="scope.row.reportedVisit != null" class="mdi mdi-check-circle planned-report-done-icon"></i>
                      <span>{{ formatDate(scope.row.reportedVisitDate) }}</span>
                    </span>
                  </template>
                </el-table-column>

                <el-table-column :label="$t('clients.visitsList.lastVisitDate')" min-width="130">
                  <template slot-scope="scope">
                    <span>{{ formatDate(scope.row.lastVisitDate) }}</span>
                  </template>
                </el-table-column>

                <el-table-column :label="$t('common.Actions')" min-width="150" align="center">
                  <template slot-scope="scope">
                    <div class="visit-row-actions">
                      <el-tooltip :content="scope.row.reportedVisit != null ? $t('clients.visitsList.viewPlannedVisitReport') : $t('clients.visitsList.createVisitReport')" placement="top">
                        <el-button
                          type="text"
                          :disabled="scope.row.customerId == null"
                          @click="scope.row.reportedVisit != null ? openPlannedVisitReport(scope.row) : openCreateVisitReport(scope.row)">
                          <i :class="scope.row.reportedVisit != null ? 'mdi mdi-clipboard-check-outline' : 'el-icon-circle-plus-outline'"></i>
                        </el-button>
                      </el-tooltip>
                      <el-tooltip :content="$t('clients.visitsList.editPlanningTooltip')" placement="top">
                        <el-button
                          type="text"
                          icon="el-icon-edit"
                          :disabled="scope.row.plannedVisit == null && scope.row.plannedVisitSource == null"
                          @click="openPlannedVisitEditor(scope.row)">
                        </el-button>
                      </el-tooltip>
                      <el-dropdown trigger="click">
                        <el-button type="text" icon="el-icon-more"></el-button>
                        <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item
                            :disabled="scope.row.nextVisitDate == null"
                            @click.native="downloadPlannedVisitIcs(scope.row)">
                            {{ $t('clients.visitsList.downloadToCalendar') }}
                          </el-dropdown-item>
                          <el-dropdown-item
                            :disabled="scope.row.lastVisit == null"
                            @click.native="openLastVisitReport(scope.row)">
                            {{$t('clients.visitsList.viewLastVisitReport')}}
                          </el-dropdown-item>
                          <el-dropdown-item
                            :disabled="scope.row.plannedVisit == null && scope.row.plannedVisitSource == null"
                            @click.native="handleDeletePlannedVisit(scope.row)">
                            {{ $t('clients.visitsList.deletePlanning') }}
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </el-dropdown>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <aside class="planned-layout__calendar card-base card-shadow--small">
              <div class="week-calendar__title-row">
                <div class="week-calendar__title">{{ $t('clients.visitsList.weekCalendarTitle') }}</div>
                <div class="week-calendar__nav">
                  <el-button
                    type="text"
                    icon="el-icon-arrow-left"
                    :disabled="!canGoPreviousWeek"
                    @click="goToPreviousWeek">
                  </el-button>
                  <el-button
                    type="text"
                    icon="el-icon-arrow-right"
                    @click="goToNextWeek">
                  </el-button>
                </div>
              </div>
              <div class="week-calendar__range">{{ weekRangeLabel }}</div>
              <div class="week-calendar">
                <div v-for="day in weeklyCalendarDays" :key="day.key" class="week-calendar__day">
                  <div class="week-calendar__day-header">
                    <span class="week-calendar__day-label">{{ day.label }}</span>
                    <span class="week-calendar__day-count">{{ day.visits.length }}</span>
                  </div>
                  <div v-if="day.visits.length === 0" class="week-calendar__empty">-</div>
                  <div v-else class="week-calendar__items">
                    <el-popover
                      v-for="visit in day.visits"
                      :key="`wk-${day.key}-${visit.id || visit.visitId || visit.customerId || visit.customerLabel}`"
                      placement="right-start"
                      trigger="hover"
                      width="340"
                      popper-class="week-calendar-entry-popover">
                      <div class="week-calendar-entry-card">
                        <div class="week-calendar-entry-card__title-row">
                          <div class="week-calendar-entry-card__title">{{ visit.customerLabel }}</div>
                          <div class="week-calendar-entry-card__meta">
                            <el-tooltip :content="visit.typeLabel" placement="top">
                              <i :class="visit.typeIcon" class="week-calendar-entry-card__type"></i>
                            </el-tooltip>
                            <span class="week-calendar-entry-card__appointment" style="padding-left:5px">{{ visit.appointmentDateTimeLabel }}</span>
                          </div>
                        </div>

                        <div class="week-calendar-entry-card__section week-calendar-entry-card__section--contact">
                          <br>
                          <div class="week-calendar-entry-card__section-title">
                            
                            <span>{{ $t('clients.visitsList.infoSectionTitle') }}</span>
                          </div>
                          <div class="week-calendar-entry-card__line" style="padding-left:10px">
                            <span class="week-calendar-entry-card__line-icon"><i class="mdi mdi-account-outline"></i></span>
                            <span class="week-calendar-entry-card__line-value" style="padding-left:5px">{{ visit.contactName || '-' }}</span>
                          </div>
                          <div class="week-calendar-entry-card__line" style="padding-left:10px">
                            <span class="week-calendar-entry-card__line-icon"><i class="mdi mdi-email-outline"></i></span>
                            <span class="week-calendar-entry-card__line-value" style="padding-left:5px">{{ visit.contactEmail || '-' }}</span>
                          </div>
                          <div class="week-calendar-entry-card__line" style="padding-left:10px">
                            <span class="week-calendar-entry-card__line-icon"><i class="mdi mdi-phone-outline"></i></span>
                            <span class="week-calendar-entry-card__line-value" style="padding-left:5px">{{ visit.contactPhone || '-' }}</span>
                          </div>
                        </div>

                        <div class="week-calendar-entry-card__section">
                          <br>
                          <div class="week-calendar-entry-card__address">
                            <span class="week-calendar-entry-card__line-icon"><i class="mdi mdi-map-marker-outline"></i></span>
                            <span class="week-calendar-entry-card__line-value">{{ visit.addressLabel || '-' }}</span>
                          </div>
                          <div v-if="visit.addressEmbedUrl" class="week-calendar-entry-card__map-frame-wrap">
                            <iframe
                              class="week-calendar-entry-card__map-frame"
                              :src="visit.addressEmbedUrl"
                              loading="lazy"
                              referrerpolicy="no-referrer-when-downgrade"
                              :title="$t('clients.visitsList.addressMapPreviewTitle')">
                            </iframe>
                          </div>
                          <a
                            v-if="visit.addressMapsUrl"
                            :href="visit.addressMapsUrl"
                            class="week-calendar-entry-card__map-link"
                            target="_blank"
                            rel="noopener noreferrer">
                            {{ $t('clients.openMap') }}
                          </a>
                        </div>
                      </div>

                      <div slot="reference" class="week-calendar__item">
                        <span class="week-calendar__item-time">{{ visit.timeLabel }}</span>
                        <el-tooltip :content="visit.typeLabel" placement="top">
                          <i :class="visit.typeIcon" class="week-calendar__item-type"></i>
                        </el-tooltip>
                        <el-tooltip v-if="visit.hasReportedVisit" :content="$t('clients.visitsList.reportCompletedTooltip')" placement="top">
                          <i class="mdi mdi-check-circle week-calendar__item-done"></i>
                        </el-tooltip>
                        <button
                          v-if="visit.customerId != null"
                          type="button"
                          class="week-calendar__item-customer week-calendar__item-customer--action"
                          @click="handlePlannedVisitNameClick(visit)">
                          {{ visit.customerLabel }}
                        </button>
                        <span v-else class="week-calendar__item-customer">{{ visit.customerLabel }}</span>
                      </div>
                    </el-popover>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane name="all" :label="$t('clients.visitsList.allReportsTabTitle')">
        <div class="visit-section card-base card-shadow--small">
          <div class="visit-section__header">
            <h4 class="visit-section__title">{{ $t('clients.visitsList.allReportsTabTitle') }}</h4>
          </div>

          <div class="visit-section__filters mb-10">
            <el-radio-group v-model="allVisitsPeriodFilter" size="small" class="all-visits-period-filter">
              <el-radio-button label="WEEK">{{ $t('clients.visitsList.filters.thisWeek') }}</el-radio-button>
              <el-radio-button label="MONTH">{{ $t('clients.visitsList.filters.thisMonth') }}</el-radio-button>
              <el-radio-button label="ALL">{{ $t('clients.visitsList.allSectionTitle') }}</el-radio-button>
            </el-radio-group>
            <el-select
              v-if="canSelectRepresentative"
              v-model="selectedRepresentativeId"
              size="small"
              :clearable="canSelectRepresentative"
              :disabled="!canSelectRepresentative"
              filterable
              class="visits-representative-select"
              :placeholder="$t('clients.visitsList.allRepresentatives')">
              <el-option
                v-for="representative in representativeOptions"
                :key="`all-rep-${representative.id}`"
                :label="representative.label"
                :value="representative.id">
              </el-option>
            </el-select>
            <el-date-picker
              v-model="allVisitsDateRange"
              type="daterange"
              class="all-visits-date-range"
              size="small"
              unlink-panels
              clearable
              range-separator="-"
              :start-placeholder="$t('common.startDate')"
              :end-placeholder="$t('common.endDate')"
              value-format="yyyy-MM-dd"
              format="yyyy-MM-dd">
            </el-date-picker>
            <el-input
              v-model="allVisitsCustomerSearch"
              clearable
              size="small"
              class="all-visits-search"
              :placeholder="$t('clients.visitsList.searchByCustomer')"
              prefix-icon="el-icon-search">
            </el-input>
          </div>

          <div v-if="showRepresentativeDebug" class="visit-rep-debug-banner">
            {{ $t('clients.visitsList.repDebugBanner', {
              selectedRepresentativeId: selectedRepresentativeId || '-',
              effectiveRepresentativeId: effectiveRepresentativeId || '-',
              optionsCount: representativeOptions.length,
            }) }}
          </div>

          <div class="all-kpis">
            <div class="all-kpi-card card-base card-shadow--small all-kpi-card--total">
              <div class="all-kpi-card__icon-wrap">
                <i class="mdi mdi-format-list-bulleted-square all-kpi-card__icon"></i>
              </div>
              <div class="all-kpi-card__content">
                <div class="all-kpi-card__value">{{ allSectionKpis.totalCount }}</div>
                <div class="all-kpi-card__label">{{ $t('clients.visitsList.visitsInListLabel') }}</div>
                <div class="all-kpi-card__hint">{{ $t('clients.visitsList.periodWithActiveSearchHint', { period: allVisitsPeriodLabel }) }}</div>
              </div>
            </div>

            <div class="all-kpi-card card-base card-shadow--small all-kpi-card--types">
              <div class="all-kpi-card__header">
                <div class="all-kpi-card__title">{{ $t('clients.visitsList.visitTypeShareTitle') }}</div>
                <div class="all-kpi-card__hint">{{ $t('clients.visitsList.basedOnActiveFiltersHint') }}</div>
              </div>

              <div v-if="allTypeKpis.length === 0" class="all-kpi-card__empty">
                {{ $t('clients.visitsList.noVisitsForFilter') }}
              </div>

              <div v-else class="all-type-kpis">
                <div v-for="typeKpi in allTypeKpis" :key="`all-${typeKpi.code}`" class="all-type-kpi-row">
                  <div class="all-type-kpi-row__main">
                    <span class="all-type-kpi-row__type">
                      <i :class="typeKpi.icon"></i>
                      <span>{{ typeKpi.label }}</span>
                    </span>
                    <span class="all-type-kpi-row__values">{{ typeKpi.count }} | {{ typeKpi.percentage }}%</span>
                  </div>
                  <div class="all-type-kpi-row__track">
                    <span class="all-type-kpi-row__bar" :style="{ width: typeKpi.percentage + '%' }"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <el-table
            v-loading="loading"
            :data="allVisitsRows"
            :default-sort="{ prop: 'date', order: 'descending' }"
            size="mini"
            stripe>
            <el-table-column :label="$t('common.date')" min-width="130" prop="date" sortable :sort-method="sortByVisitDate">
              <template slot-scope="scope">
                <span>{{ formatDate(getVisitDate(scope.row)) }}</span>
              </template>
            </el-table-column>

            <el-table-column :label="$t('clients.name')" min-width="220">
              <template slot-scope="scope">
                <router-link
                  v-if="getVisitCustomerId(scope.row) != null"
                  :to="`/customers/${getVisitCustomerId(scope.row)}`"
                  class="visit-customer-link">
                  {{ getVisitCustomerLabel(scope.row) }}
                </router-link>
                <span v-else>{{ getVisitCustomerLabel(scope.row) }}</span>
                <div v-if="showRepresentativeDebug" class="visit-rep-debug">
                  {{ getRepresentativeDebugInfo(scope.row) }}
                </div>
              </template>
            </el-table-column>

            <el-table-column :label="$t('clients.type')" min-width="90" align="center" :filters="visitTypeFilters" :filter-method="filterVisitType" column-key="visitType">
              <template slot-scope="scope">
                <el-tooltip :content="visitTypeLabel(getVisitTypeCode(scope.row))" placement="top">
                  <i :class="getVisitTypeIcon(getVisitTypeCode(scope.row))"></i>
                </el-tooltip>
              </template>
            </el-table-column>

            <el-table-column :label="$t('clients.contacts')" min-width="250" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ getVisitContactsSummary(scope.row) }}</span>
              </template>
            </el-table-column>

            <el-table-column :label="$t('clients.nextVisitDate')" min-width="130">
              <template slot-scope="scope">
                <span>{{ formatDate(getVisitNextDate(scope.row)) }}</span>
              </template>
            </el-table-column>

            <el-table-column :label="$t('clients.tabs.notes')" min-width="260" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ getVisitNotes(scope.row) }}</span>
              </template>
            </el-table-column>

            <el-table-column v-if="$can('write', 'Customer')" :label="$t('common.Actions')" min-width="120" align="center">
              <template slot-scope="scope">
                <div class="visit-row-actions">
                  <el-tooltip :content="$t('clients.editVisitTooltip')" placement="top">
                    <el-button
                      type="text"
                      icon="el-icon-edit"
                      @click="openVisitEditor(scope.row)">
                    </el-button>
                  </el-tooltip>
                  <el-tooltip :content="$t('clients.deleteVisitTooltip')" placement="top">
                    <el-button
                      type="text"
                      icon="el-icon-delete"
                      class="danger"
                      @click="handleDeleteVisit(scope.row)">
                    </el-button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>

    <VisitCreationDrawer
      :visible="visitDrawerVisible"
      :customerId="visitDrawerCustomerId"
      :customerName="visitDrawerCustomerName"
      :contacts="visitDrawerContacts"
      :mode="visitDrawerMode"
      :visitToEdit="visitToEdit"
      @close="closeVisitDrawer"
      @saved="handleVisitSaved" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import VisitAPI from '@/services/API/visit';
import PortausLink from '@/components/portaus-link';
import VisitCreationDrawer from '@/components/visit/visit-creation-drawer.vue';

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);

const VISIT_DOMAINS = [
  { code: 'VISIT', labelKey: 'clients.visitTypeLabels.visit', icon: 'mdi mdi-office-building' },
  { code: 'QUICK_CALL', labelKey: 'clients.visitTypeLabels.quickCall', icon: 'mdi mdi-phone-fast' },
  { code: 'PHONE_CALL', labelKey: 'clients.visitTypeLabels.phoneCall', icon: 'mdi mdi-phone' },
  { code: 'EMAIL-SMS', labelKey: 'clients.visitTypeLabels.emailSms', icon: 'mdi mdi-email-outline' },
  { code: 'DELIVERY-PICK_UP', labelKey: 'clients.visitTypeLabels.deliveryPickUp', icon: 'mdi mdi-truck-delivery-outline' },
  { code: 'DEGUSTATION', labelKey: 'clients.visitTypeLabels.degustation', icon: 'mdi mdi-glass-wine' },
  { code: 'OTHER', labelKey: 'clients.visitTypeLabels.other', icon: 'mdi mdi-shape-outline' },
];

function normalizeText(value) {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
}

export default {
  name: 'VisitsListPage',
  components: {
    PortausLink,
    VisitCreationDrawer,
  },
  data() {
    return {
      loading: false,
      visits: [],
      activeVisitTab: 'planned',
      selectedRepresentativeId: null,
      allVisitsCustomerSearch: '',
      allVisitsPeriodFilter: 'ALL',
      allVisitsDateRange: [],
      plannedPeriodFilter: 'FUTURE',
      weekOffset: 0,
      visitDrawerVisible: false,
      visitToEdit: null,
      visitDrawerMode: 'report',
      visitDrawerCustomerId: null,
      visitDrawerCustomerName: '',
      visitDrawerContacts: [],
      plannedVisitReportedLinks: {},
      fetchVisitsDebounceId: null,
      fetchVisitsRequestId: 0,
    };
  },
  watch: {
    '$route.query': {
      handler() {
        this.syncStateFromRouteQuery();
      },
      immediate: false,
    },
    currentUserId(newCurrentUserId) {
      if (!this.isCurrentUserAdmin && newCurrentUserId != null && this.selectedRepresentativeId !== newCurrentUserId) {
        this.selectedRepresentativeId = newCurrentUserId;
      }
    },
    activeVisitTab() {
      this.scheduleFetchVisits();
    },
    selectedRepresentativeId() {
      this.scheduleFetchVisits();
    },
    plannedPeriodFilter() {
      if (this.activeVisitTab === 'planned') {
        this.scheduleFetchVisits();
      }
    },
    allVisitsPeriodFilter() {
      if (this.activeVisitTab === 'all') {
        this.scheduleFetchVisits();
      }
    },
    allVisitsDateRange() {
      if (this.activeVisitTab === 'all') {
        this.scheduleFetchVisits();
      }
    },
  },
  computed: {
    isCurrentUserAdmin() {
      return this.$store && this.$store.getters && this.$store.getters.isCurrentUserAdmin === true;
    },
    currentUserId() {
      const userIdFromGetter = this.$store && this.$store.getters
        ? this.$store.getters.getCurrentUserId
        : null;
      const fallbackUser = this.$store && this.$store.getters
        ? this.$store.getters.user
        : null;
      const fallbackUserId = fallbackUser && fallbackUser.id != null ? fallbackUser.id : null;
      const resolvedUserId = userIdFromGetter != null ? userIdFromGetter : fallbackUserId;
      return resolvedUserId != null ? String(resolvedUserId) : null;
    },
    canSelectRepresentative() {
      return this.isCurrentUserAdmin;
    },
    showRepresentativeDebug() {
      const query = this.$route && this.$route.query ? this.$route.query : {};
      return this.isCurrentUserAdmin && String(query.repDebug || '') === '1';
    },
    effectiveRepresentativeId() {
      if (this.isCurrentUserAdmin) {
        return this.selectedRepresentativeId != null ? String(this.selectedRepresentativeId) : null;
      }

      return this.currentUserId;
    },
    allVisitsRows() {
      const reportVisits = this.filteredVisits.filter((visit) => !this.isPlannedVisit(visit));
      const periodFiltered = reportVisits.filter((visit) => this.isVisitMatchingAllFilter(this.getVisitDate(visit)));
      const dateRangeFiltered = periodFiltered.filter((visit) => this.isVisitMatchingSelectedDateRange(this.getVisitDate(visit)));
      const normalizedSearch = normalizeText(this.allVisitsCustomerSearch);
      const filtered = dateRangeFiltered.filter((visit) => {
        if (normalizedSearch === '') return true;
        const customerLabel = normalizeText(this.getVisitCustomerLabel(visit));
        return customerLabel.indexOf(normalizedSearch) !== -1;
      });

      return filtered.slice().sort((left, right) => {
        const leftTs = this.getVisitTimestamp(left);
        const rightTs = this.getVisitTimestamp(right);
        if (leftTs === rightTs) return 0;
        return leftTs > rightTs ? -1 : 1;
      });
    },
    filteredVisits() {
      return this.visits.filter((visit) => this.isVisitMatchingRepresentative(visit));
    },
    representativeOptions() {
      const usersGetter = this.$store && this.$store.getters
        ? this.$store.getters['users/filterUserWithRoleRep']
        : [];

      const reps = Array.isArray(usersGetter) ? usersGetter : [];

      const sortedRepresentatives = reps
        .map((representative) => ({
          id: representative != null && representative.id != null ? String(representative.id) : null,
          label: this.getRepresentativeOptionLabel(representative),
        }))
        .filter((representative) => representative.id != null && representative.id !== '')
        .sort((left, right) => left.label.localeCompare(right.label, this.$i18n.locale));

      if (this.isCurrentUserAdmin) {
        return sortedRepresentatives;
      }

      const currentUserId = this.currentUserId;
      if (currentUserId == null) {
        return [];
      }

      const currentRepresentative = sortedRepresentatives.find((representative) => representative.id === currentUserId);
      if (currentRepresentative != null) {
        return [currentRepresentative];
      }

      return [{
        id: currentUserId,
        label: this.getCurrentUserDisplayLabel(),
      }];
    },
    visitTypeFilters() {
      return VISIT_DOMAINS.map((domain) => ({
        text: this.$t(domain.labelKey),
        value: domain.code,
      }));
    },
    allSectionKpis() {
      return {
        totalCount: this.allVisitsRows.length,
      };
    },
    allVisitsPeriodLabel() {
      if (this.allVisitsPeriodFilter === 'WEEK') return this.$t('clients.visitsList.currentWeekPeriod');
      if (this.allVisitsPeriodFilter === 'MONTH') return this.$t('clients.visitsList.currentMonthPeriod');
      return this.$t('clients.visitsList.allPeriods');
    },
    allTypeKpis() {
      const rows = this.allVisitsRows;
      const total = rows.length;

      return VISIT_DOMAINS
        .map((domain) => {
          const count = rows.filter((row) => this.getVisitTypeCode(row) === domain.code).length;
          return {
            code: domain.code,
            label: this.$t(domain.labelKey),
            icon: domain.icon,
            count,
            percentage: total > 0 ? Math.round((count / total) * 100) : 0,
          };
        })
        .filter((typeKpi) => typeKpi.count > 0)
        .sort((left, right) => {
          if (left.count === right.count) {
            return left.label.localeCompare(right.label, this.$i18n.locale);
          }
          return right.count - left.count;
        });
    },
    plannedSectionKpis() {
      const plannedRows = this.plannedCustomersRows;
      const plannedCount = plannedRows.length;
      const reportedCount = plannedRows.filter((row) => row.reportedVisit != null).length;
      const completionRate = plannedCount > 0
        ? Math.round((reportedCount / plannedCount) * 100)
        : 0;

      return {
        plannedCount,
        reportedCount,
        completionRate,
      };
    },
    plannedTypeKpis() {
      const plannedRows = this.plannedCustomersRows;
      const total = plannedRows.length;

      return VISIT_DOMAINS
        .map((domain) => {
          const count = plannedRows.filter((row) => this.getVisitTypeCode(row.plannedVisitSource || row.plannedVisit || row) === domain.code).length;
          return {
            code: domain.code,
            label: this.$t(domain.labelKey),
            icon: domain.icon,
            count,
            percentage: total > 0 ? Math.round((count / total) * 100) : 0,
          };
        })
        .filter((typeKpi) => typeKpi.count > 0)
        .sort((left, right) => {
          if (left.count === right.count) {
            return left.label.localeCompare(right.label, this.$i18n.locale);
          }
          return right.count - left.count;
        });
    },
    plannedTypeChartSegments() {
      const palette = ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399', '#8e44ad'];
      const total = this.plannedSectionKpis.plannedCount;
      const topSegments = this.plannedTypeKpis.slice(0, 4).map((segment, index) => ({
        ...segment,
        color: palette[index % palette.length],
      }));

      const otherSegments = this.plannedTypeKpis.slice(4);
      const otherCount = otherSegments.reduce((sum, segment) => sum + segment.count, 0);
      if (otherCount > 0) {
        topSegments.push({
          code: 'OTHER_TYPES',
          label: 'Autres',
          icon: 'mdi mdi-dots-horizontal',
          count: otherCount,
          percentage: total > 0 ? Math.round((otherCount / total) * 100) : 0,
          color: palette[5],
        });
      }

      return topSegments;
    },
    plannedTypePieGradient() {
      const segments = this.plannedTypeChartSegments;
      const total = segments.reduce((sum, segment) => sum + segment.count, 0);

      if (total <= 0) {
        return 'conic-gradient(#ebeef5 0deg, #ebeef5 360deg)';
      }

      let start = 0;
      const parts = segments.map((segment) => {
        const slice = (segment.count / total) * 100;
        const end = start + slice;
        const part = `${segment.color} ${start}% ${end}%`;
        start = end;
        return part;
      });

      return `conic-gradient(${parts.join(', ')})`;
    },
    canGoPreviousWeek() {
      return this.weekOffset > 0;
    },
    weekRangeLabel() {
      const startOfWeek = dayjs().startOf('isoWeek').add(this.weekOffset, 'week').format('YYYY-MM-DD');
      const endOfWeek = dayjs().startOf('isoWeek').add(this.weekOffset, 'week').endOf('isoWeek').format('YYYY-MM-DD');
      return `${startOfWeek} - ${endOfWeek}`;
    },
    plannedCustomersRows() {
      const plannedVisits = this.filteredVisits.filter((visit) => {
        const plannedDate = this.getPlannedVisitDate(visit);
        return this.isVisitMatchingPlannedFilter(plannedDate);
      });

      const rows = plannedVisits.map((visit) => {
        const customerIdRaw = this.getVisitCustomerId(visit);
        const customerIdKey = customerIdRaw != null ? String(customerIdRaw) : '__NO_CUSTOMER__';
        const customerAllVisits = this.filteredVisits.filter((candidateVisit) => {
          if (this.isPlannedVisit(candidateVisit)) {
            return false;
          }
          const visitCustomerId = this.getVisitCustomerId(candidateVisit);
          const normalizedVisitCustomerId = visitCustomerId != null ? String(visitCustomerId) : '__NO_CUSTOMER__';
          return normalizedVisitCustomerId === customerIdKey;
        });

        const latestVisit = customerAllVisits.slice().sort((left, right) => {
          const leftTs = this.getVisitTimestamp(left);
          const rightTs = this.getVisitTimestamp(right);
          if (leftTs === rightTs) return 0;
          return leftTs > rightTs ? -1 : 1;
        })[0] || null;

        const plannedVisitId = visit && (visit.id || visit.visitId) != null
          ? Number(visit.id || visit.visitId)
          : null;

        const linkedReportedVisit = customerAllVisits
          .filter((candidateVisit) => {
            const linkedPlannedVisitId = this.getLinkedPlannedVisitId(candidateVisit);
            return linkedPlannedVisitId != null && plannedVisitId != null && linkedPlannedVisitId === plannedVisitId;
          })
          .sort((left, right) => {
            const leftTs = this.getVisitTimestamp(left);
            const rightTs = this.getVisitTimestamp(right);
            if (leftTs === rightTs) return 0;
            return leftTs > rightTs ? -1 : 1;
          })[0] || null;

        return {
          customerId: customerIdRaw != null ? customerIdRaw : null,
          customerLabel: this.getVisitCustomerLabel(visit),
          nextVisitDate: this.getPlannedVisitDate(visit),
          nextVisitStartTime: this.getVisitStartTime(visit),
          nextVisitEndTime: this.getVisitEndTime(visit),
          nextVisitDurationMinutes: this.getVisitDurationMinutes(visit),
          plannedVisit: this.isPlannedVisit(visit) ? visit : null,
          plannedVisitSource: visit,
          plannedVisitsCount: 1,
          isSyntheticPlannedVisit: !this.isPlannedVisit(visit),
          reportedVisitDate: linkedReportedVisit != null ? this.getVisitDate(linkedReportedVisit) : null,
          reportedVisit: linkedReportedVisit,
          lastVisitDate: latestVisit != null ? this.getVisitDate(latestVisit) : null,
          lastVisit: latestVisit,
        };
      });

      return rows.sort((left, right) => {
        const leftTs = this.getDateTimestamp(left.nextVisitDate);
        const rightTs = this.getDateTimestamp(right.nextVisitDate);
        if (leftTs === rightTs) {
          return left.customerLabel.localeCompare(right.customerLabel, this.$i18n.locale);
        }
        return leftTs > rightTs ? 1 : -1;
      });
    },
    weeklyCalendarDays() {
      const startOfWeek = dayjs().startOf('isoWeek').add(this.weekOffset, 'week');
      const adjustedEndOfWeek = dayjs(startOfWeek).endOf('isoWeek');

      const plannedThisWeek = this.filteredVisits.filter((visit) => {
        const plannedDateValue = this.getPlannedVisitDate(visit);
        if (plannedDateValue == null || plannedDateValue === '') return false;
        const nextDate = dayjs(plannedDateValue);
        if (!nextDate.isValid()) return false;
        return nextDate.isBetween(startOfWeek, adjustedEndOfWeek, null, '[]');
      });

      const days = [];
      for (let dayOffset = 0; dayOffset < 7; dayOffset += 1) {
        const day = dayjs(startOfWeek).add(dayOffset, 'day');
        const dayKey = day.format('YYYY-MM-DD');
        const dayVisits = plannedThisWeek
          .filter((visit) => {
            const nextDate = dayjs(this.getPlannedVisitDate(visit));
            return nextDate.isValid() && nextDate.format('YYYY-MM-DD') === dayKey;
          })
          .map((visit) => {
            const startMinutes = this.extractTimeMinutes(this.getVisitStartTime(visit));
            const typeCode = this.getVisitTypeCode(visit);
            const plannedDateValue = this.getPlannedVisitDate(visit);
            const plannedDateLabel = this.formatDate(plannedDateValue);
            const timeLabel = startMinutes == null ? '--:--' : this.formatTimeFromMinutes(startMinutes);
            const primaryContact = this.getVisitPrimaryContact(visit);
            const preferredAddress = this.getPreferredCustomerAddress(visit && visit.customer ? visit.customer : null);
            const addressLabel = this.formatAddressLabel(preferredAddress);
            const plannedVisitId = this.getNormalizedVisitId(visit);
            const hasReportedVisit = this.hasReportedVisitForPlannedId(plannedVisitId);
            return {
              id: visit.id || visit.visitId || null,
              customerId: this.getVisitCustomerId(visit),
              customerLabel: this.getVisitCustomerLabel(visit),
              typeCode,
              typeIcon: this.getVisitTypeIcon(typeCode),
              typeLabel: this.visitTypeLabel(typeCode),
              contactName: this.getContactDisplayName(primaryContact),
              contactEmail: this.getContactWorkEmail(primaryContact),
              contactPhone: this.getContactWorkPhone(primaryContact),
              addressLabel,
              addressMapsUrl: this.buildAddressMapsUrl(addressLabel),
              addressEmbedUrl: this.buildAddressEmbedUrl(addressLabel),
              appointmentDateTimeLabel: timeLabel === '--:--' ? plannedDateLabel : `${plannedDateLabel} ${timeLabel}`,
              startMinutes,
              timeLabel,
              hasReportedVisit,
            };
          })
          .sort((left, right) => left.customerLabel.localeCompare(right.customerLabel, this.$i18n.locale));

        dayVisits.sort((left, right) => {
          if (left.startMinutes == null && right.startMinutes == null) {
            return left.customerLabel.localeCompare(right.customerLabel, this.$i18n.locale);
          }
          if (left.startMinutes == null) return 1;
          if (right.startMinutes == null) return -1;
          if (left.startMinutes === right.startMinutes) {
            return left.customerLabel.localeCompare(right.customerLabel, this.$i18n.locale);
          }
          return left.startMinutes > right.startMinutes ? 1 : -1;
        });

        days.push({
          key: dayKey,
          label: day.format('ddd DD'),
          visits: dayVisits,
        });
      }

      return days;
    },
  },
  methods: {
    getNormalizedVisitId(visit) {
      const rawId = _.get(visit, 'id') || _.get(visit, 'visitId') || null;
      if (rawId == null) return null;
      const normalizedId = Number(rawId);
      return Number.isNaN(normalizedId) ? String(rawId) : normalizedId;
    },
    hasReportedVisitForPlannedId(plannedVisitId) {
      if (plannedVisitId == null) {
        return false;
      }

      const directLink = _.get(this.plannedVisitReportedLinks, String(plannedVisitId));
      if (directLink != null) {
        return true;
      }

      return this.plannedCustomersRows.some((row) => {
        const rowPlannedSource = row.plannedVisit || row.plannedVisitSource || null;
        const rowPlannedId = this.getNormalizedVisitId(rowPlannedSource);
        return rowPlannedId != null
          && String(rowPlannedId) === String(plannedVisitId)
          && row.reportedVisit != null;
      });
    },
    buildVisitIcsContent(visit, row) {
      const visitAddressLabel = this.getVisitIcsAddressLabel(visit);
      const summary = this.$t('clients.visitsList.icsSummary', {
        customer: this.getVisitCustomerLabel(visit) || row.customerLabel || this.$t('clients.visitsList.customerFallback'),
      });
      const descriptionParts = [
        this.$t('clients.visitsList.icsDescriptionHeader'),
        this.$t('clients.visitsList.icsDescriptionCustomer', {
          customer: this.getVisitCustomerLabel(visit) || row.customerLabel || '-',
        }),
        this.$t('clients.visitsList.icsDescriptionDate', {
          date: this.formatDate(this.getVisitNextDate(visit)),
        }),
      ];

      if (visitAddressLabel != null && visitAddressLabel !== '') {
        descriptionParts.push(this.$t('clients.visitsList.icsDescriptionAddress', { address: visitAddressLabel }));
      }

      const location = visitAddressLabel || this.getVisitCustomerLabel(visit) || row.customerLabel || '';
      const times = this.getVisitIcsTimes(visit);
      const uid = `${this.getVisitCustomerId(visit) || row.customerId || this.getVisitCustomerLabel(visit) || 'visit'}-${this.getVisitNextDate(visit) || Date.now()}@portaus`;
      const stamp = dayjs().utc().format('YYYYMMDDTHHmmss[Z]');

      const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Portaus//Visit Planner//FR',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${this.escapeIcsText(uid)}`,
        `DTSTAMP:${stamp}`,
        `SUMMARY:${this.escapeIcsText(summary)}`,
        `DESCRIPTION:${this.escapeIcsText(descriptionParts.join('\n'))}`,
      ];

      if (location) {
        lines.push(`LOCATION:${this.escapeIcsText(location)}`);
      }

      if (times.allDay === true) {
        lines.push(`DTSTART;VALUE=DATE:${times.start.format('YYYYMMDD')}`);
        lines.push(`DTEND;VALUE=DATE:${times.end.format('YYYYMMDD')}`);
      } else {
        lines.push(`DTSTART:${times.start.format('YYYYMMDDTHHmmss')}`);
        lines.push(`DTEND:${times.end.format('YYYYMMDDTHHmmss')}`);
      }

      lines.push('END:VEVENT');
      lines.push('END:VCALENDAR');

      return `${lines.join('\r\n')}\r\n`;
    },
    getVisitIcsAddressLabel(visit) {
      if (visit == null) {
        return null;
      }

      const customer = visit.customer || null;
      if (customer == null) {
        return null;
      }

      const preferredAddress = this.getPreferredCustomerAddress(customer);
      return this.formatAddressLabel(preferredAddress);
    },
    getVisitIcsTimes(visit) {
      const baseDateValue = this.getVisitNextDate(visit);
      const baseDate = dayjs(baseDateValue);

      if (!baseDate.isValid()) {
        const today = dayjs().startOf('day');
        return {
          allDay: true,
          start: today,
          end: today.clone().add(1, 'day'),
        };
      }

      const startTimeValue = this.getVisitStartTime(visit);
      const startMinutes = this.extractTimeMinutes(startTimeValue);

      if (startMinutes == null) {
        const start = baseDate.clone().startOf('day');
        return {
          allDay: true,
          start,
          end: start.clone().add(1, 'day'),
        };
      }

      const start = baseDate.clone().startOf('day').add(startMinutes, 'minutes');
      const endTimeValue = this.getVisitEndTime(visit);
      const endTimeMinutes = this.extractTimeMinutes(endTimeValue);

      let end = null;
      if (endTimeMinutes != null) {
        end = baseDate.clone().startOf('day').add(endTimeMinutes, 'minutes');
      }

      if (end == null) {
        const durationMinutes = this.getVisitDurationMinutes(visit);
        end = start.clone().add(durationMinutes, 'minutes');
      }

      if (!end.isAfter(start)) {
        end = start.clone().add(30, 'minutes');
      }

      return {
        allDay: false,
        start,
        end,
      };
    },
    getVisitEndTime(visit) {
      return (visit && (visit.endTime || visit.end_time || visit.nextVisitEndTime || visit.next_visit_end_time)) || null;
    },
    getVisitDurationMinutes(visit) {
      const explicitDuration = visit && (visit.nextVisitDurationMinutes || visit.next_visit_duration_minutes || visit.durationMinutes || visit.duration_minutes);
      const parsedDuration = Number(explicitDuration);
      if (Number.isFinite(parsedDuration) && parsedDuration > 0) {
        return parsedDuration;
      }

      const startTimeValue = this.getVisitStartTime(visit);
      const endTimeValue = this.getVisitEndTime(visit);
      const startMinutes = this.extractTimeMinutes(startTimeValue);
      const endMinutes = this.extractTimeMinutes(endTimeValue);

      if (startMinutes != null && endMinutes != null) {
        let duration = endMinutes - startMinutes;
        if (duration <= 0) {
          duration += 24 * 60;
        }
        if (duration > 0) {
          return duration;
        }
      }

      return 30;
    },
    escapeIcsText(value) {
      if (value == null) {
        return '';
      }

      return String(value)
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n');
    },
    slugifyFileName(value) {
      return String(value == null ? 'visit' : value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() || 'visit';
    },
    downloadTextFile(filename, content, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    downloadPlannedVisitIcs(row) {
      if (row == null) {
        return;
      }

      const sourceVisit = row.plannedVisitSource || row.plannedVisit || row;
      const visitForExport = {
        ...sourceVisit,
        nextVisitDate: this.getVisitNextDate(sourceVisit) || row.nextVisitDate || this.getPlannedVisitDate(sourceVisit),
        next_visit_date: _.get(sourceVisit, 'next_visit_date') || row.nextVisitDate || this.getPlannedVisitDate(sourceVisit),
        startTime: this.getVisitStartTime(sourceVisit) || row.nextVisitStartTime || _.get(sourceVisit, 'start_time') || null,
        start_time: _.get(sourceVisit, 'start_time') || row.nextVisitStartTime || this.getVisitStartTime(sourceVisit) || null,
        endTime: this.getVisitEndTime(sourceVisit) || row.nextVisitEndTime || _.get(sourceVisit, 'end_time') || null,
        end_time: _.get(sourceVisit, 'end_time') || row.nextVisitEndTime || this.getVisitEndTime(sourceVisit) || null,
        nextVisitDurationMinutes: row.nextVisitDurationMinutes || _.get(sourceVisit, 'nextVisitDurationMinutes') || _.get(sourceVisit, 'next_visit_duration_minutes') || null,
      };

      const nextVisitDate = this.getVisitNextDate(visitForExport);

      if (nextVisitDate == null || nextVisitDate === '') {
        this.$message.warning(this.$t('clients.visitsList.noPlannedDateForCalendarExport'));
        return;
      }

      const icsContent = this.buildVisitIcsContent(visitForExport, row);
      const customerLabel = this.getVisitCustomerLabel(visitForExport) || row.customerLabel || this.$t('clients.visitsList.customerFallback').toLowerCase();
      const dateLabel = this.formatDate(nextVisitDate) || dayjs().format('YYYY-MM-DD');
      const filename = `${this.slugifyFileName(this.$t('clients.visitsList.plannedVisitFilePrefix'))}-${this.slugifyFileName(customerLabel)}-${this.slugifyFileName(dateLabel)}.ics`;

      this.downloadTextFile(filename, icsContent, 'text/calendar;charset=utf-8');
    },
    scheduleFetchVisits() {
      if (this.fetchVisitsDebounceId != null) {
        clearTimeout(this.fetchVisitsDebounceId);
      }

      this.fetchVisitsDebounceId = setTimeout(() => {
        this.fetchVisitsDebounceId = null;
        this.fetchVisits();
      }, 120);
    },
    getCurrentPeriodBounds(period) {
      if (period === 'WEEK') {
        return {
          dateFrom: dayjs().startOf('isoWeek').format('YYYY-MM-DD'),
          dateTo: dayjs().endOf('isoWeek').format('YYYY-MM-DD'),
        };
      }

      if (period === 'MONTH') {
        return {
          dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
          dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
        };
      }

      return null;
    },
    buildVisitFetchParams() {
      const params = { paging: 'no' };

      if (this.activeVisitTab === 'all') {
        params.status = 'REPORTED';

        const periodBounds = this.getCurrentPeriodBounds(this.allVisitsPeriodFilter);
        if (periodBounds != null) {
          params.dateFrom = periodBounds.dateFrom;
          params.dateTo = periodBounds.dateTo;
        }

        if (Array.isArray(this.allVisitsDateRange)
          && this.allVisitsDateRange.length === 2
          && this.allVisitsDateRange[0] != null
          && this.allVisitsDateRange[1] != null) {
          params.dateFrom = this.allVisitsDateRange[0];
          params.dateTo = this.allVisitsDateRange[1];
        }
      } else {
        params.status = ['PLANNED', 'REPORTED'];

        const periodBounds = this.getCurrentPeriodBounds(this.plannedPeriodFilter);
        if (periodBounds != null) {
          params.dateFrom = periodBounds.dateFrom;
          params.dateTo = periodBounds.dateTo;
        }
      }

      if (this.effectiveRepresentativeId != null && this.effectiveRepresentativeId !== '') {
        params.repId = this.effectiveRepresentativeId;
      }

      return params;
    },
    fetchVisits() {
      const requestId = this.fetchVisitsRequestId + 1;
      this.fetchVisitsRequestId = requestId;
      const params = this.buildVisitFetchParams();
      this.loading = true;
      return VisitAPI.fetchVisits(params)
        .then((visitsResponse) => {
          if (requestId !== this.fetchVisitsRequestId) {
            return;
          }
          this.visits = this.applyPlannedVisitReportedLinks(
            this.sanitizeVisits(this.normalizeVisitList(visitsResponse))
          );
        })
        .catch((error) => {
          if (requestId !== this.fetchVisitsRequestId) {
            return;
          }
          console.error('[VisitsListPage] Unable to fetch visits', error);
          this.visits = [];
          this.$message.error(this.$t('clients.loadingVisits'));
        })
        .finally(() => {
          if (requestId === this.fetchVisitsRequestId) {
            this.loading = false;
          }
        });
    },
    normalizeVisitList(visitsResponse) {
      if (Array.isArray(visitsResponse)) return visitsResponse;
      if (visitsResponse && Array.isArray(visitsResponse.items)) return visitsResponse.items;
      if (visitsResponse && Array.isArray(visitsResponse.results)) return visitsResponse.results;
      if (visitsResponse && Array.isArray(visitsResponse.list)) return visitsResponse.list;
      if (visitsResponse && Array.isArray(visitsResponse.rows)) return visitsResponse.rows;
      if (visitsResponse && Array.isArray(visitsResponse.data)) return visitsResponse.data;
      if (visitsResponse && visitsResponse.data && Array.isArray(visitsResponse.data.list)) return visitsResponse.data.list;
      if (visitsResponse && visitsResponse.data && Array.isArray(visitsResponse.data.items)) return visitsResponse.data.items;
      return [];
    },
    sanitizeVisits(visits) {
      if (!Array.isArray(visits)) return [];
      return visits.filter((visit) => visit != null && typeof visit === 'object');
    },
    applyPlannedVisitReportedLinks(visits) {
      if (!Array.isArray(visits) || Object.keys(this.plannedVisitReportedLinks).length === 0) {
        return visits;
      }

      return visits.map((visit) => {
        const visitId = visit && (visit.id || visit.visitId);
        if (visitId == null || this.getLinkedPlannedVisitId(visit) != null) {
          return visit;
        }

        const matchedPlannedVisitId = Object.keys(this.plannedVisitReportedLinks).find((plannedVisitId) => {
          const reportedVisitId = _.get(this.plannedVisitReportedLinks, `${plannedVisitId}.id`);
          return String(reportedVisitId) === String(visitId);
        });

        if (matchedPlannedVisitId == null) {
          return visit;
        }

        return {
          ...visit,
          plannedVisit: {
            id: Number(matchedPlannedVisitId),
          },
        };
      });
    },
    normalizeText(value) {
      if (value == null) return '';
      return String(value).trim().toLowerCase();
    },
    filterVisitType(typeCode, row) {
      return this.getVisitTypeCode(row) === typeCode;
    },
    sortByVisitDate(left, right) {
      const leftTs = this.getVisitTimestamp(left);
      const rightTs = this.getVisitTimestamp(right);
      if (leftTs === rightTs) return 0;
      return leftTs > rightTs ? 1 : -1;
    },
    syncStateFromRouteQuery() {
      const query = this.$route && this.$route.query ? this.$route.query : {};
      const tab = query.tab;
      const period = query.period;

      if (tab === 'planned' || tab === 'all') {
        this.activeVisitTab = tab;
      }

      if (period === 'WEEK' || period === 'MONTH' || period === 'FUTURE') {
        this.plannedPeriodFilter = period;
      }

      if (period === 'WEEK' || period === 'MONTH' || period === 'ALL') {
        this.allVisitsPeriodFilter = period;
      }
    },
    goToNextWeek() {
      this.weekOffset += 1;
    },
    goToPreviousWeek() {
      if (this.weekOffset <= 0) {
        return;
      }
      this.weekOffset -= 1;
    },
    getVisitDate(visit) {
      return (visit && (visit.date || visit.visitDate || visit.visitedAt || visit.created_at)) || null;
    },
    getVisitStartTime(visit) {
      return (visit && (visit.startTime || visit.start_time || visit.nextVisitStartTime || visit.next_visit_start_time)) || null;
    },
    extractTimeMinutes(timeValue) {
      if (timeValue == null || timeValue === '') {
        return null;
      }
      const asString = String(timeValue).trim();
      const match = asString.match(/(\d{2}):(\d{2})/);
      if (match == null) {
        return null;
      }
      const hour = Number(match[1]);
      const minute = Number(match[2]);
      if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return null;
      }
      return (hour * 60) + minute;
    },
    formatTimeFromMinutes(minutesValue) {
      if (!Number.isFinite(minutesValue)) {
        return '-';
      }
      const safeMinutes = Math.max(0, Math.min(1439, Math.round(minutesValue)));
      const hour = String(Math.floor(safeMinutes / 60)).padStart(2, '0');
      const minute = String(safeMinutes % 60).padStart(2, '0');
      return `${hour}:${minute}`;
    },
    isVisitMatchingPlannedFilter(nextDateValue) {
      if (nextDateValue == null || nextDateValue === '') return false;
      const nextDate = dayjs(nextDateValue);
      if (!nextDate.isValid()) return false;

      const normalizedNextDate = nextDate.startOf('day');
      const today = dayjs().startOf('day');

      if (this.plannedPeriodFilter === 'WEEK') {
        const weekStart = dayjs().startOf('isoWeek');
        const weekEnd = dayjs().endOf('isoWeek');
        return normalizedNextDate.isBetween(weekStart, weekEnd, null, '[]');
      }

      if (this.plannedPeriodFilter === 'MONTH') {
        const monthStart = dayjs().startOf('month');
        const monthEnd = dayjs().endOf('month');
        return normalizedNextDate.isBetween(monthStart, monthEnd, null, '[]');
      }

      return normalizedNextDate.isSameOrAfter(today);
    },
    isVisitMatchingAllFilter(visitDateValue) {
      if (this.allVisitsPeriodFilter === 'ALL') {
        return true;
      }

      if (visitDateValue == null || visitDateValue === '') {
        return false;
      }

      const visitDate = dayjs(visitDateValue);
      if (!visitDate.isValid()) {
        return false;
      }

      if (this.allVisitsPeriodFilter === 'WEEK') {
        const weekStart = dayjs().startOf('isoWeek');
        const weekEnd = dayjs().endOf('isoWeek');
        return visitDate.isBetween(weekStart, weekEnd, null, '[]');
      }

      if (this.allVisitsPeriodFilter === 'MONTH') {
        const monthStart = dayjs().startOf('month');
        const monthEnd = dayjs().endOf('month');
        return visitDate.isBetween(monthStart, monthEnd, null, '[]');
      }

      return true;
    },
    isVisitMatchingSelectedDateRange(visitDateValue) {
      const selectedRange = this.allVisitsDateRange;

      if (!Array.isArray(selectedRange) || selectedRange.length !== 2 || selectedRange[0] == null || selectedRange[1] == null) {
        return true;
      }

      if (visitDateValue == null || visitDateValue === '') {
        return false;
      }

      const visitDate = dayjs(visitDateValue);
      if (!visitDate.isValid()) {
        return false;
      }

      const startDate = dayjs(selectedRange[0]).startOf('day');
      const endDate = dayjs(selectedRange[1]).endOf('day');

      if (!startDate.isValid() || !endDate.isValid()) {
        return true;
      }

      return visitDate.isBetween(startDate, endDate, null, '[]');
    },
    isVisitMatchingRepresentative(visit) {
      const selectedRepresentativeId = this.effectiveRepresentativeId;
      if (selectedRepresentativeId == null || selectedRepresentativeId === '') {
        return true;
      }

      return this.getVisitRepresentativeId(visit) === selectedRepresentativeId;
    },
    getCurrentUserDisplayLabel() {
      const user = this.$store && this.$store.getters ? this.$store.getters.user : null;
      if (user != null) {
        const firstName = user.first_name || user.firstName || '';
        const lastName = user.last_name || user.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName !== '') {
          return fullName;
        }

        const fallbackLabel = user.name || user.username || user.email;
        if (fallbackLabel != null && String(fallbackLabel).trim() !== '') {
          return String(fallbackLabel);
        }
      }

      const currentUserId = this.currentUserId;
      return currentUserId != null
        ? this.$t('clients.visitsList.representativeNumber', { id: currentUserId })
        : this.$t('clients.visitsList.myAccount');
    },
    getVisitRepresentativeId(visit) {
      const representativeId = _.get(visit, 'rep.id')
        || _.get(visit, 'rep:id')
        || (visit && typeof visit.rep !== 'object' ? visit.rep : null)
        || _.get(visit, 'repId')
        || _.get(visit, 'rep_id')
        || _.get(visit, 'fk_rep_id')
        || _.get(visit, 'fkRepId')
        || _.get(visit, 'fkRepID')
        || _.get(visit, 'representative.id')
        || _.get(visit, 'salesRep.id')
        || _.get(visit, 'userId')
        || _.get(visit, 'fk_user_id')
        || null;

      if (representativeId == null || representativeId === '') {
        return null;
      }

      return String(representativeId);
    },
    getVisitRepresentativeLabel(visit) {
      const representative = visit && visit.rep ? visit.rep : null;

      if (representative != null) {
        const firstName = representative.first_name || representative.firstName || '';
        const lastName = representative.last_name || representative.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        if (fullName !== '') {
          return fullName;
        }

        const representativeName = representative.name || representative.username || representative.email || representative.work_email;
        if (representativeName != null && String(representativeName).trim() !== '') {
          return String(representativeName);
        }
      }

      const representativeId = this.getVisitRepresentativeId(visit);
      return representativeId != null
        ? this.$t('clients.visitsList.representativeNumber', { id: representativeId })
        : this.$t('clients.visitsList.unknownRepresentative');
    },
    getRepresentativeOptionLabel(representative) {
      if (representative == null || typeof representative !== 'object') {
        return this.$t('clients.visitsList.unknownRepresentative');
      }

      const firstName = representative.firstName || representative.first_name || '';
      const lastName = representative.lastName || representative.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName !== '') {
        return fullName;
      }

      const fallbackLabel = representative.name || representative.username || representative.email || representative.work_email;
      if (fallbackLabel != null && String(fallbackLabel).trim() !== '') {
        return String(fallbackLabel);
      }

      return representative.id != null
        ? this.$t('clients.visitsList.representativeNumber', { id: representative.id })
        : this.$t('clients.visitsList.unknownRepresentative');
    },
    getRepresentativeDebugInfo(visit) {
      const detectedRepresentativeId = this.getVisitRepresentativeId(visit);
      const candidates = {
        'rep.id': _.get(visit, 'rep.id'),
        'rep:id': _.get(visit, 'rep:id'),
        rep: visit && typeof visit.rep !== 'object' ? visit.rep : null,
        repId: _.get(visit, 'repId'),
        rep_id: _.get(visit, 'rep_id'),
        fk_rep_id: _.get(visit, 'fk_rep_id'),
        fkRepId: _.get(visit, 'fkRepId'),
        'representative.id': _.get(visit, 'representative.id'),
        'salesRep.id': _.get(visit, 'salesRep.id'),
        userId: _.get(visit, 'userId'),
        fk_user_id: _.get(visit, 'fk_user_id'),
      };

      const sources = Object.keys(candidates)
        .filter((candidateKey) => candidates[candidateKey] != null && String(candidates[candidateKey]).trim() !== '')
        .map((candidateKey) => `${candidateKey}=${candidates[candidateKey]}`)
        .join(' | ');

      if (sources === '') {
        return this.$t('clients.visitsList.repDebugSourceNotFound', {
          detectedRepresentativeId: detectedRepresentativeId || '-',
        });
      }

      return this.$t('clients.visitsList.repDebugWithSources', {
        detectedRepresentativeId: detectedRepresentativeId || '-',
        sources,
      });
    },
    getVisitNextDate(visit) {
      return (visit && (visit.nextVisitDate || visit.next_visit_date)) || null;
    },
    getVisitTimestamp(visit) {
      const dateValue = visit && (visit.date || visit.visitDate || visit.visitedAt || visit.created_at);
      return this.getDateTimestamp(dateValue);
    },
    getVisitNextTimestamp(visit) {
      const dateValue = this.getVisitNextDate(visit);
      return this.getDateTimestamp(dateValue);
    },
    getDateTimestamp(dateValue) {
      const date = dayjs(dateValue);
      if (!date.isValid()) return 0;
      return date.valueOf();
    },
    formatDate(dateValue) {
      if (dateValue == null || dateValue === '') return '-';
      const date = dayjs(dateValue);
      if (!date.isValid()) return '-';
      return date.format('YYYY-MM-DD');
    },
    getVisitTypeCode(visit) {
      if (!visit) return 'OTHER';
      return visit.visitType && visit.visitType.code
        ? visit.visitType.code
        : visit.visit_type && visit.visit_type.code
          ? visit.visit_type.code
          : visit.type && visit.type.code
            ? visit.type.code
            : visit.type
              ? visit.type
              : 'OTHER';
    },
    getVisitDomains() {
      return VISIT_DOMAINS.map((domain) => ({
        ...domain,
        label: this.$t(domain.labelKey),
      }));
    },
    visitTypeLabel(code) {
      const domain = this.getVisitDomains().find((item) => item.code === code);
      return domain ? domain.label : code;
    },
    getVisitTypeIcon(code) {
      const domain = VISIT_DOMAINS.find((item) => item.code === code);
      return domain ? domain.icon : 'mdi mdi-shape-outline';
    },
    getVisitPrimaryContact(visit) {
      const visitContacts = this.getVisitContactsArray(visit);
      if (visitContacts.length > 0) {
        return visitContacts[0];
      }

      const customer = visit && visit.customer ? visit.customer : null;
      if (customer == null) {
        return null;
      }

      const companyPrimaryContacts = _.get(customer, 'company.primaryContacts');
      if (Array.isArray(companyPrimaryContacts) && companyPrimaryContacts.length > 0) {
        return companyPrimaryContacts[0];
      }

      const companyContacts = _.get(customer, 'company.contacts');
      if (Array.isArray(companyContacts) && companyContacts.length > 0) {
        const flaggedPrimary = companyContacts.find((contact) => contact && contact.primary === true);
        return flaggedPrimary || companyContacts[0];
      }

      if (_.isObject(customer.contact)) {
        return customer.contact;
      }

      return null;
    },
    getContactDisplayName(contact) {
      if (!contact) return null;
      const firstName = contact.first_name || contact.firstName || '';
      const lastName = contact.last_name || contact.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName !== '') return fullName;
      return contact.name || null;
    },
    getContactWorkEmail(contact) {
      if (!contact) return null;
      return contact.work_email || contact.workEmail || contact.email || null;
    },
    getContactWorkPhone(contact) {
      if (!contact) return null;
      const phone = contact.work_phone || contact.workPhone || contact.phone || null;
      const ext = contact.work_phone_ext || contact.workPhoneExt || contact.phone_ext || null;
      if (phone == null || phone === '') {
        return null;
      }
      if (ext == null || ext === '') {
        return phone;
      }
      return `${phone} ext. ${ext}`;
    },
    getPreferredCustomerAddress(customer) {
      if (!customer) return null;

      const addresses = this.getCustomerAddresses(customer);
      if (!Array.isArray(addresses) || addresses.length === 0) {
        return null;
      }

      if (addresses.length === 1) {
        return addresses[0];
      }

      const byPriority = [
        ['LIVRAISON', 'DELIVERY', 'SHIPPING'],
        ['PRINCIPALE', 'MAIN', 'PRIMARY'],
        ['FACTURATION', 'BILLING'],
      ];

      for (let i = 0; i < byPriority.length; i += 1) {
        const candidates = byPriority[i];
        const found = addresses.find((address) => {
          const code = this.getAddressTypeCode(address);
          return code != null && candidates.some((candidate) => code.indexOf(candidate) !== -1);
        });
        if (found) {
          return found;
        }
      }

      return addresses[0];
    },
    getCustomerAddresses(customer) {
      const customerAddresses = _.get(customer, 'addresses');
      if (Array.isArray(customerAddresses)) {
        return customerAddresses;
      }
      const companyAddresses = _.get(customer, 'company.addresses');
      if (Array.isArray(companyAddresses)) {
        return companyAddresses;
      }
      return [];
    },
    getAddressTypeCode(address) {
      if (!address) return null;
      const typeCode = _.get(address, 'type.code') || _.get(address, 'addressType.code') || _.get(address, 'type.name') || _.get(address, 'addressType.name') || _.get(address, 'type') || _.get(address, 'address_type');
      if (typeCode == null) return null;
      return String(typeCode).trim().toUpperCase();
    },
    formatAddressLabel(address) {
      if (!address) return null;
      const parts = [
        address.street,
        address.city,
        address.postal_code || address.postalCode,
        _.get(address, 'state.code') || _.get(address, 'state.name') || address.other_state,
        _.get(address, 'country.code') || _.get(address, 'country.name'),
      ].filter((part) => part != null && String(part).trim() !== '');

      if (parts.length === 0) {
        return null;
      }

      return parts.join(', ');
    },
    buildAddressMapsUrl(addressLabel) {
      if (addressLabel == null || addressLabel === '') {
        return null;
      }
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLabel)}`;
    },
    buildAddressEmbedUrl(addressLabel) {
      if (addressLabel == null || addressLabel === '') {
        return null;
      }
      return `https://maps.google.com/maps?q=${encodeURIComponent(addressLabel)}&t=&z=14&ie=UTF8&iwloc=&output=embed`;
    },
    getVisitCustomerId(visit) {
      if (!visit) return null;
      const customer = visit.customer || null;
      if (customer && customer.id != null) return customer.id;
      if (visit.customerId != null) return visit.customerId;
      if (visit.fk_customer_id != null) return visit.fk_customer_id;
      return null;
    },
    getVisitCustomerLabel(visit) {
      if (!visit) return '-';
      const customer = visit.customer || null;
      if (customer == null) return '-';

      const usual = [customer.usual_name, customer.usualName]
        .map((value) => (value == null ? '' : String(value).trim()))
        .find((value) => value !== '');
      const name = [customer.name]
        .map((value) => (value == null ? '' : String(value).trim()))
        .find((value) => value !== '');

      if (customer.type === 0) {
        return usual || name || '-';
      }

      const fullName = `${usual || name}`.trim();
      if (fullName !== '') return fullName;

      const firstName = customer.first_name || customer.firstName || '';
      const lastName = customer.last_name || customer.lastName || '';
      const personName = `${firstName} ${lastName}`.trim();
      return personName !== '' ? personName : '-';
    },
    getVisitContactsSummary(visit) {
      if (!visit) return '-';
      const visitedContacts = visit.visitedContacts || visit.visited_contacts || visit.contacts || [];
      if (!Array.isArray(visitedContacts) || visitedContacts.length === 0) {
        return '-';
      }

      const names = visitedContacts
        .map((contact) => {
          if (!contact) return '-';
          const firstName = contact.first_name || contact.firstName || '';
          const lastName = contact.last_name || contact.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          return fullName || contact.name || contact.email || '-';
        })
        .filter((value) => value && value !== '-');

      return names.length > 0 ? names.join(', ') : '-';
    },
    getVisitNotes(visit) {
      if (!visit) return '-';
      return visit.notes || visit.comment || visit.description || '-';
    },
    getVisitContactsArray(visit) {
      if (!visit) return [];
      const contacts = visit.visitedContacts || visit.visited_contacts || visit.contacts || [];
      if (!Array.isArray(contacts)) return [];
      return contacts.filter((contact) => contact != null && typeof contact === 'object');
    },
    getLinkedPlannedVisitId(visit) {
      const plannedVisitId = _.get(visit, 'plannedVisit.id') || _.get(visit, 'planned_visit.id') || _.get(visit, 'plannedVisitId') || _.get(visit, 'fk_planned_visit_id') || null;
      if (plannedVisitId == null) {
        return null;
      }
      const normalizedPlannedVisitId = Number(plannedVisitId);
      return Number.isNaN(normalizedPlannedVisitId) ? plannedVisitId : normalizedPlannedVisitId;
    },
    isPlannedVisit(visit) {
      return _.toUpper(_.get(visit, 'status')) === 'PLANNED';
    },
    getPlannedVisitDate(visit) {
      if (this.isPlannedVisit(visit)) {
        return this.getVisitDate(visit);
      }
      return this.getVisitNextDate(visit);
    },
    openLastVisitReport(row) {
      const visit = row && row.lastVisit ? row.lastVisit : null;
      if (visit == null) {
        this.$message.warning(this.$t('clients.noVisitRecorded'));
        return;
      }

      const customerIdRaw = this.getVisitCustomerId(visit);
      const customerId = customerIdRaw != null ? Number(customerIdRaw) : null;
      this.visitToEdit = visit;
      this.visitDrawerMode = 'report';
      this.visitDrawerCustomerId = Number.isNaN(customerId) ? null : customerId;
      this.visitDrawerCustomerName = this.getVisitCustomerLabel(visit);
      this.visitDrawerContacts = this.getVisitContactsArray(visit);
      this.visitDrawerVisible = true;
    },
    openPlannedVisitReport(row) {
      const visit = row && row.reportedVisit ? row.reportedVisit : null;
      if (visit == null) {
        this.$message.warning(this.$t('clients.visitsList.noReportAssociatedWithPlannedVisit'));
        return;
      }

      const customerIdRaw = this.getVisitCustomerId(visit);
      const customerId = customerIdRaw != null ? Number(customerIdRaw) : null;
      this.visitToEdit = visit;
      this.visitDrawerMode = 'report';
      this.visitDrawerCustomerId = Number.isNaN(customerId) ? null : customerId;
      this.visitDrawerCustomerName = this.getVisitCustomerLabel(visit);
      this.visitDrawerContacts = this.getVisitContactsArray(visit);
      this.visitDrawerVisible = true;
    },
    openCreateVisitReport(row) {
      if (row == null || row.customerId == null) {
        this.$message.warning(this.$t('clients.noVisitRecorded'));
        return;
      }

      if (row.reportedVisit != null) {
        this.$message.warning(this.$t('clients.visitsList.reportAlreadyExistsForPlannedVisit'));
        return;
      }

      const customerId = Number(row.customerId);
      this.visitToEdit = row.isSyntheticPlannedVisit ? null : row.plannedVisit || null;
      this.visitDrawerMode = 'report';
      this.visitDrawerCustomerId = Number.isNaN(customerId) ? null : customerId;
      this.visitDrawerCustomerName = row.customerLabel || '-';
      this.visitDrawerContacts = row.plannedVisit
        ? this.getVisitContactsArray(row.plannedVisit)
        : row.plannedVisitSource
          ? this.getVisitContactsArray(row.plannedVisitSource)
        : row.lastVisit
          ? this.getVisitContactsArray(row.lastVisit)
          : [];
      this.visitDrawerVisible = true;
    },
    openPlanningVisit() {
      this.visitToEdit = null;
      this.visitDrawerMode = 'planning';
      this.visitDrawerCustomerId = null;
      this.visitDrawerCustomerName = '';
      this.visitDrawerContacts = [];
      this.visitDrawerVisible = true;
    },
    buildEditablePlannedVisit(row) {
      const sourceVisit = row && (row.plannedVisit || row.plannedVisitSource || row) ? (row.plannedVisit || row.plannedVisitSource || row) : null;
      if (sourceVisit == null) {
        return null;
      }

      const visitTypeCode = this.getVisitTypeCode(sourceVisit);
      const plannedDate = this.getPlannedVisitDate(sourceVisit) || (row && row.nextVisitDate) || null;
      const startTime = this.getVisitStartTime(sourceVisit) || (row && row.nextVisitStartTime) || null;
      const endTime = this.getVisitEndTime(sourceVisit) || (row && row.nextVisitEndTime) || null;
      const notes = this.getVisitNotes(sourceVisit);
      const visitedContacts = this.getVisitContactsArray(sourceVisit);

      return {
        ...sourceVisit,
        id: sourceVisit.id || sourceVisit.visitId || sourceVisit.visit_id || null,
        visitId: sourceVisit.visitId || sourceVisit.id || sourceVisit.visit_id || null,
        status: 'PLANNED',
        date: sourceVisit.date || sourceVisit.visitDate || sourceVisit.visit_date || plannedDate,
        visitDate: sourceVisit.visitDate || sourceVisit.date || sourceVisit.visit_date || plannedDate,
        visit_date: sourceVisit.visit_date || sourceVisit.visitDate || sourceVisit.date || plannedDate,
        nextVisitDate: sourceVisit.nextVisitDate || sourceVisit.next_visit_date || plannedDate,
        next_visit_date: sourceVisit.next_visit_date || sourceVisit.nextVisitDate || plannedDate,
        startTime: sourceVisit.startTime || sourceVisit.start_time || sourceVisit.nextVisitStartTime || sourceVisit.next_visit_start_time || startTime,
        start_time: sourceVisit.start_time || sourceVisit.startTime || sourceVisit.next_visit_start_time || sourceVisit.nextVisitStartTime || startTime,
        endTime: sourceVisit.endTime || sourceVisit.end_time || sourceVisit.nextVisitEndTime || sourceVisit.next_visit_end_time || endTime,
        end_time: sourceVisit.end_time || sourceVisit.endTime || sourceVisit.next_visit_end_time || sourceVisit.nextVisitEndTime || endTime,
        notes: notes === '-' ? '' : notes,
        comment: sourceVisit.comment || (notes === '-' ? '' : notes),
        visitType: sourceVisit.visitType || { code: visitTypeCode },
        visit_type: sourceVisit.visit_type || { code: visitTypeCode },
        typeCode: sourceVisit.typeCode || visitTypeCode,
        visitTypeCode: sourceVisit.visitTypeCode || visitTypeCode,
        visit_type_code: sourceVisit.visit_type_code || visitTypeCode,
        type: sourceVisit.type || visitTypeCode,
        visitedContacts: Array.isArray(sourceVisit.visitedContacts) ? sourceVisit.visitedContacts : visitedContacts,
        visited_contacts: Array.isArray(sourceVisit.visited_contacts) ? sourceVisit.visited_contacts : visitedContacts,
        contacts: Array.isArray(sourceVisit.contacts) ? sourceVisit.contacts : visitedContacts,
        visitedContactIds: visitedContacts.map((contact) => contact && contact.id).filter((contactId) => contactId != null),
      };
    },
    openPlannedVisitEditor(row) {
      if (row == null) {
        return;
      }

      const editableVisit = this.buildEditablePlannedVisit(row);
      if (editableVisit == null) {
        return;
      }

      this.openVisitEditor(editableVisit);
    },
    buildVisitUpdatePayload(visit, overrides = {}) {
      const payload = {
        customerId: this.getVisitCustomerId(visit),
        plannedVisitId: this.getLinkedPlannedVisitId(visit),
        visitDate: this.getVisitDate(visit),
        visitType: this.getVisitTypeCode(visit),
        visitedContactIds: this.getVisitContactsArray(visit).map((contact) => contact.id).filter((contactId) => contactId != null),
        status: _.toUpper(_.get(visit, 'status')) || 'REPORTED',
        products: Array.isArray(visit && visit.products) ? visit.products : [],
        nextVisitDate: this.getVisitNextDate(visit),
        startTime: this.getVisitStartTime(visit),
        endTime: this.getVisitEndTime(visit),
        notes: _.get(visit, 'notes') || '',
      };

      return {
        ...payload,
        ...overrides,
      };
    },
    handleDeletePlannedVisit(row) {
      if (row == null) {
        return;
      }

      if (!row.isSyntheticPlannedVisit && row.plannedVisit != null) {
        this.handleDeleteVisit(row.plannedVisit);
        return;
      }

      const sourceVisit = row.plannedVisitSource || null;
      const sourceVisitId = sourceVisit && (sourceVisit.id || sourceVisit.visitId);
      if (sourceVisitId == null) {
        this.$message.error(this.$i18n.t('clients.deleteVisitMissingId'));
        return;
      }

      this.$confirm(this.$i18n.t('clients.visitsList.deletePlanningConfirm'), this.$i18n.t('common.Warning'), {
        confirmButtonText: this.$i18n.t('common.yes'),
        cancelButtonText: this.$i18n.t('common.Cancel'),
        type: 'warning',
      }).then(() => {
        return VisitAPI.getVisit(sourceVisitId)
          .then((visit) => {
            const payload = this.buildVisitUpdatePayload(visit || sourceVisit, {
              nextVisitDate: null,
              startTime: null,
              endTime: null,
            });
            return VisitAPI.updateVisit(sourceVisitId, payload);
          })
          .then(() => {
            this.$message.success(this.$t('clients.visitsList.deletePlanningSuccess'));
            this.fetchVisits();
          })
          .catch(() => {
            this.$message.error(this.$t('clients.visitsList.deletePlanningError'));
          });
      }).catch(() => {
        // cancellation: no-op
      });
    },
    openVisitEditor(visit) {
      if (visit == null) {
        return;
      }
      const customerIdRaw = this.getVisitCustomerId(visit);
      const customerId = customerIdRaw != null ? Number(customerIdRaw) : null;
      this.visitToEdit = visit;
      this.visitDrawerMode = this.isPlannedVisit(visit) ? 'planning' : 'report';
      this.visitDrawerCustomerId = Number.isNaN(customerId) ? null : customerId;
      this.visitDrawerCustomerName = this.getVisitCustomerLabel(visit);
      this.visitDrawerContacts = this.getVisitContactsArray(visit);
      this.visitDrawerVisible = true;
    },
    handleDeleteVisit(visit) {
      const visitId = visit && (visit.id || visit.visitId);
      if (visitId == null) {
        this.$message.error(this.$i18n.t('clients.deleteVisitMissingId'));
        return;
      }

      this.$confirm(this.$i18n.t('clients.deleteVisitConfirm'), this.$i18n.t('common.Warning'), {
        confirmButtonText: this.$i18n.t('common.yes'),
        cancelButtonText: this.$i18n.t('common.Cancel'),
        type: 'warning',
      }).then(() => {
        return VisitAPI.deleteVisit(visitId)
          .then(() => {
            this.$message.success(this.$i18n.t('clients.deleteVisitSuccess'));
            this.fetchVisits();
          })
          .catch(() => {
            this.$message.error(this.$i18n.t('clients.deleteVisitError'));
          });
      }).catch(() => {
        // cancellation: no-op
      });
    },
    closeVisitDrawer() {
      this.visitDrawerVisible = false;
      this.visitToEdit = null;
      this.visitDrawerCustomerId = null;
      this.visitDrawerCustomerName = '';
      this.visitDrawerContacts = [];
    },
    handleVisitSaved(savedVisit, context = {}) {
      if (context.isReportConversion === true && context.plannedVisitId != null && savedVisit != null) {
        this.$set(this.plannedVisitReportedLinks, String(context.plannedVisitId), {
          id: savedVisit.id || savedVisit.visitId,
          visit: savedVisit,
        });
      }
      this.closeVisitDrawer();
      this.fetchVisits();
    },
  },
  created() {
    this.syncStateFromRouteQuery();
    if (this.isCurrentUserAdmin) {
      this.$store.dispatch('users/fetchAdminUsers').catch(() => {
        // Ignore representative list loading errors and fallback to visits-based filtering only.
      });
    }
    if (!this.isCurrentUserAdmin && this.currentUserId != null) {
      this.selectedRepresentativeId = this.currentUserId;
    }
    this.fetchVisits();
  },
  mounted() {
    document.body.classList.add('page-visits-active');
  },
  beforeDestroy() {
    if (this.fetchVisitsDebounceId != null) {
      clearTimeout(this.fetchVisitsDebounceId);
      this.fetchVisitsDebounceId = null;
    }
    document.body.classList.remove('page-visits-active');
  },
};
</script>

<style lang="scss" scoped>
.page-visits {
  .visit-top-nav-shell {
    position: sticky;
    top: 0;
    z-index: 20;
    margin-top: 10px;
    margin-bottom: 12px;
    padding-bottom: 6px;
  }

  .visit-top-nav-card {
    border-radius: 12px;
    border: 1px solid #e6edf5;
    background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
    box-shadow: 0 3px 10px rgba(17, 24, 39, 0.06);
  }

  .visit-top-nav-card ::v-deep .el-card__body {
    padding: 10px 12px;
  }

  .visit-top-nav {
    display: grid;
    gap: 10px;
  }

  .visit-page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #303133;
    line-height: 1.2;
  }

  .visit-section {
    padding: 14px;
  }

  .visit-tabs {
    margin-top: 6px;
  }

  ::v-deep .visit-tabs--hidden-header .el-tabs__header {
    display: none;
  }

  .visit-tab-switch {
    display: inline-grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    padding: 6px;
    border-radius: 12px;
    margin-bottom: 0;
    background: #f4f6f8;
    border: 1px solid #e5e9f0;
    width: 100%;
    max-width: 520px;
  }

  .visit-tab-switch__item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 0;
    border-radius: 9px;
    padding: 10px 14px;
    background: transparent;
    color: #606266;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .16s ease;
    min-height: 38px;
  }

  .visit-tab-switch__item:hover {
    color: #303133;
    background: rgba(255, 255, 255, 0.7);
  }

  .visit-tab-switch__item--active {
    background: #ffffff;
    color: #2f3b52;
    box-shadow: 0 1px 3px rgba(33, 43, 54, 0.12);
  }

  .visit-tab-switch__icon {
    font-size: 16px;
    line-height: 1;
  }

  .visit-section__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 10px;
  }

  .visit-section__title {
    margin: 0;
    font-size: 16px;
  }

  .visit-section__header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .visit-section__filters {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .visit-rep-debug-banner {
    margin: -2px 0 10px;
    padding: 6px 9px;
    border-radius: 6px;
    border: 1px dashed #e6a23c;
    background: #fff8eb;
    color: #8a5a00;
    font-size: 12px;
    line-height: 1.3;
  }

  .visit-rep-debug {
    margin-top: 4px;
    font-size: 11px;
    color: #909399;
    line-height: 1.25;
    word-break: break-word;
  }

  .planned-kpis {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 14px;
  }

  .planned-kpi-card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
    min-height: 116px;
  }

  .planned-kpi-card__content {
    min-width: 0;
    width: 100%;
  }

  .planned-kpi-card__content--planned {
    display: grid;
    gap: 10px;
  }

  .planned-kpi-card__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .planned-kpi-card__pie-wrap {
    flex: 0 0 auto;
  }

  .planned-kpi-card__pie {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    position: relative;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.8);
  }

  .planned-kpi-card__pie-hole {
    position: absolute;
    inset: 12px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .planned-kpi-card__pie-hole span {
    font-size: 16px;
    font-weight: 700;
    color: #303133;
  }

  .planned-kpi-card__pie-hole small {
    margin-top: 2px;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: #909399;
  }

  .planned-kpi-card__legend {
    display: grid;
    gap: 4px;
  }

  .planned-kpi-card__legend-item {
    display: grid;
    grid-template-columns: 10px minmax(0, 1fr) auto;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    line-height: 1.25;
  }

  .planned-kpi-card__legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .planned-kpi-card__legend-label {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #606266;
  }

  .planned-kpi-card__legend-value {
    font-weight: 600;
    color: #303133;
  }

  .planned-kpi-card__icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    background: #f5f7fa;
  }

  .planned-kpi-card__icon {
    font-size: 20px;
    color: #409eff;
  }

  .planned-kpi-card__value {
    font-size: 26px;
    font-weight: 700;
    color: #303133;
    line-height: 1;
  }

  .planned-kpi-card__label {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #606266;
    line-height: 1.2;
  }

  .planned-kpi-card__hint {
    margin-top: 3px;
    font-size: 11px;
    color: #909399;
    line-height: 1.2;
  }

  .planned-kpi-card--planned .planned-kpi-card__icon-wrap {
    background: #ecf5ff;
  }

  .planned-kpi-card--reported .planned-kpi-card__icon-wrap {
    background: #f0f9eb;
  }

  .planned-kpi-card--rate .planned-kpi-card__icon-wrap {
    background: #fdf6ec;
  }

  .planned-type-kpi {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 8px;
    min-height: 76px;
  }

  .planned-type-kpi__icon-wrap {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    background: #f5f7fa;
  }

  .planned-type-kpi__icon {
    font-size: 18px;
    color: #606266;
  }

  .planned-type-kpi__content {
    min-width: 0;
  }

  .planned-type-kpi__value {
    font-size: 22px;
    font-weight: 700;
    color: #303133;
    line-height: 1;
  }

  .planned-type-kpi__label {
    margin-top: 3px;
    font-size: 13px;
    font-weight: 600;
    color: #606266;
    line-height: 1.2;
  }

  .planned-type-kpi__hint {
    margin-top: 2px;
    font-size: 11px;
    color: #909399;
    line-height: 1.2;
  }

  .all-visits-period-filter {
    flex: 0 0 auto;
  }

  .visits-representative-select {
    width: 260px;
    max-width: 100%;
  }

  .all-visits-date-range {
    width: 280px;
    max-width: 100%;
  }

  .all-visits-search {
    max-width: 320px;
  }

  .all-kpis {
    display: grid;
    grid-template-columns: minmax(0, 270px) minmax(0, 1fr);
    gap: 12px;
    margin-bottom: 14px;
  }

  .all-kpi-card {
    padding: 12px 14px;
    border-radius: 10px;
  }

  .all-kpi-card--total {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .all-kpi-card__icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ecf5ff;
    flex: 0 0 auto;
  }

  .all-kpi-card__icon {
    font-size: 20px;
    color: #409eff;
  }

  .all-kpi-card__value {
    font-size: 26px;
    font-weight: 700;
    color: #303133;
    line-height: 1;
  }

  .all-kpi-card__label {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #606266;
  }

  .all-kpi-card__hint {
    margin-top: 3px;
    font-size: 11px;
    color: #909399;
  }

  .all-kpi-card__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
  }

  .all-kpi-card__title {
    font-size: 13px;
    font-weight: 700;
    color: #303133;
  }

  .all-kpi-card__empty {
    font-size: 12px;
    color: #909399;
  }

  .all-type-kpis {
    display: grid;
    gap: 8px;
  }

  .all-type-kpi-row {
    display: grid;
    gap: 5px;
  }

  .all-type-kpi-row__main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .all-type-kpi-row__type {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    color: #4b5563;
    font-size: 12px;
    font-weight: 600;
  }

  .all-type-kpi-row__type i {
    font-size: 14px;
    color: #6b7280;
    flex: 0 0 auto;
  }

  .all-type-kpi-row__type span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .all-type-kpi-row__values {
    font-size: 12px;
    font-weight: 700;
    color: #303133;
    white-space: nowrap;
  }

  .all-type-kpi-row__track {
    height: 6px;
    border-radius: 999px;
    background: #edf2f7;
    overflow: hidden;
  }

  .all-type-kpi-row__bar {
    display: block;
    height: 100%;
    min-width: 3px;
    border-radius: inherit;
    background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%);
  }

  .planned-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 12px;
    align-items: start;
  }

  .planned-layout__calendar {
    padding: 10px;
  }

  .week-calendar__title {
    font-size: 14px;
    font-weight: 700;
    color: #303133;
  }

  .week-calendar__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .week-calendar__nav {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .week-calendar__nav .el-button {
    padding: 2px 4px;
    font-size: 16px;
  }

  .week-calendar__range {
    margin-top: 2px;
    margin-bottom: 10px;
    font-size: 12px;
    color: #909399;
  }

  .week-calendar {
    display: grid;
    gap: 8px;
  }

  .week-calendar__day {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    background: #fff;
    padding: 8px;
  }

  .week-calendar__day-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .week-calendar__day-label {
    font-size: 12px;
    font-weight: 700;
    color: #606266;
    text-transform: uppercase;
  }

  .week-calendar__day-count {
    font-size: 11px;
    min-width: 20px;
    text-align: center;
    padding: 2px 6px;
    border-radius: 10px;
    background: #ecf5ff;
    color: #409eff;
  }

  .week-calendar__empty {
    font-size: 12px;
    color: #c0c4cc;
  }

  .week-calendar__items {
    display: grid;
    gap: 4px;
  }

  .week-calendar__item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #303133;
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-left: 3px solid #409eff;
    border-radius: 4px;
    padding: 4px 6px;
    line-height: 1.25;
  }

  .week-calendar__item-time {
    font-size: 10px;
    font-weight: 700;
    color: #409eff;
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .week-calendar__item-type {
    font-size: 14px;
    color: #606266;
    flex: 0 0 auto;
    opacity: 0.92;
  }

  .week-calendar__item-done {
    font-size: 14px;
    color: #67c23a;
    flex: 0 0 auto;
  }

  .week-calendar-entry-card {
    display: grid;
    gap: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
    border: 1px solid #e6edf7;
    border-radius: 12px;
    padding: 10px;
  }

  .week-calendar-entry-card__title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #e3e8f2;
  }

  .week-calendar-entry-card__meta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: #4b5563;
  }

  .week-calendar-entry-card__title {
    font-size: 15px;
    font-weight: 800;
    color: #1f2937;
    line-height: 1.2;
    letter-spacing: .01em;
  }

  .week-calendar-entry-card__type {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    border: 1px solid #dbeafe;
    background: #eff6ff;
    font-size: 14px;
    color: #3b82f6;
    flex: 0 0 auto;
  }

  .week-calendar-entry-card__appointment {
    font-size: 10px;
    font-weight: 700;
    color: #374151;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    padding: 4px 9px;
    white-space: nowrap;
    letter-spacing: .02em;
  }

  .week-calendar-entry-card__section {
    border: 1px solid #e7edf6;
    border-radius: 8px;
    padding: 11px 10px;
    background: linear-gradient(180deg, #fcfdff 0%, #f6faff 100%);
  }

  .week-calendar-entry-card__section + .week-calendar-entry-card__section {
    margin-top: 2px;
  }

  .week-calendar-entry-card__section-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 800;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: .03em;
    margin-bottom: 11px;
  }

  .week-calendar-entry-card__section-title i {
    font-size: 14px;
    color: #64748b;
  }

  .week-calendar-entry-card__section--contact {
    margin-top: 2px;
  }

  .week-calendar-entry-card__line,
  .week-calendar-entry-card__address {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 12px;
    color: #4b5563;
    line-height: 1.45;
    padding: 8px 9px;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid #edf1f7;
    margin-bottom: 9px;
  }

  .week-calendar-entry-card__line-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    color: #5b6b82;
    font-size: 14px;
  }

  .week-calendar-entry-card__line:last-child,
  .week-calendar-entry-card__address:last-child {
    margin-bottom: 0;
  }

  .week-calendar-entry-card__line-value {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    line-height: 1.4;
    word-break: break-word;
  }

  .week-calendar-entry-card__map-link {
    margin-top: 10px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #1d4ed8;
    text-decoration: none;
    font-weight: 700;
    background: #eef4ff;
    border: 1px solid #d7e4ff;
    border-radius: 999px;
    padding: 5px 10px;
  }

  .week-calendar-entry-card__map-link:hover {
    background: #e2ecff;
    border-color: #c4d8ff;
  }

  .week-calendar-entry-card__map-frame-wrap {
    margin-top: 8px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #dfe6f0;
    background: #f4f6fb;
  }

  .week-calendar-entry-card__map-frame {
    display: block;
    width: 100%;
    height: 128px;
    border: 0;
  }

  .week-calendar__item-customer {
    font-size: 12px;
    font-weight: 700;
    color: #303133;
    line-height: 1.25;
    word-break: break-word;
    min-width: 0;
  }

  .week-calendar__item-customer--action {
    border: 0;
    background: transparent;
    padding: 0;
    color: #303133;
    cursor: pointer;
    text-align: left;
  }

  .week-calendar__item-customer--action:hover {
    text-decoration: underline;
  }

  .visit-toolbar-right {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .visit-customer-link {
    color: #409eff;
    text-decoration: none;
  }

  .visit-customer-action {
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
    color: #409eff;
    font: inherit;
    text-align: left;
  }

  .visit-customer-action:hover {
    text-decoration: underline;
  }

  .visit-customer-link:hover {
    text-decoration: underline;
  }

  .visit-row-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .planned-report-date-wrap {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .planned-report-done-icon {
    color: #67c23a;
    font-size: 14px;
    line-height: 1;
  }

  ::v-deep .el-table-filter__bottom {
    padding: 10px;
    display: flex;
    gap: 8px;
    justify-content: center;
    border-top: 1px solid #ebeef5;
  }

  ::v-deep .el-table-filter__bottom button {
    min-width: 96px;
    height: 30px;
    border-radius: 4px;
    border: 1px solid #dcdfe6 !important;
    background: #ffffff;
    color: #606266;
    font-weight: 600;
    transition: all .15s ease;
  }

  ::v-deep .el-table-filter__bottom button:hover {
    border-color: #409eff;
    color: #409eff;
    background: #ecf5ff;
  }

  ::v-deep .el-table-filter__bottom button:first-child {
    border: 1px solid #409eff !important;
    background: #409eff;
    color: #ffffff;
  }

  ::v-deep .el-table-filter__bottom button:first-child:hover {
    background: #66b1ff;
    border-color: #66b1ff;
    color: #ffffff;
  }
}

@media (max-width: 992px) {
  .page-visits {
    .visit-top-nav-shell {
      margin-top: 8px;
    }

    .planned-kpis {
      grid-template-columns: minmax(0, 1fr);
    }

    .all-kpis {
      grid-template-columns: minmax(0, 1fr);
    }

    .visit-tab-switch {
      width: 100%;
      min-width: 0;
    }

    .visit-tab-switch__item {
      padding: 10px;
    }

    .visits-representative-select,
    .all-visits-date-range,
    .all-visits-search {
      width: 100%;
      max-width: 100%;
    }

    .planned-kpi-card__top {
      align-items: flex-start;
    }

    .planned-kpi-card__pie {
      width: 64px;
      height: 64px;
    }

    .planned-kpi-card__pie-hole {
      inset: 11px;
    }

    .planned-layout {
      grid-template-columns: minmax(0, 1fr);
    }

    .visit-section__header {
      flex-direction: column;
      align-items: flex-start;
    }

    .visit-toolbar-right {
      justify-content: flex-start;
      margin-top: 8px;
    }
  }
}
</style>

<style lang="scss">
body.page-visits-active {
  .el-table-filter__bottom button {
    border: 1px solid #dcdfe6 !important;
  }

  .el-table-filter__bottom button:first-child {
    border: 1px solid #409eff !important;
  }
}

.week-calendar-entry-popover {
  padding: 8px !important;
  border-radius: 12px !important;
  border: 1px solid #dfe6f0 !important;
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.14) !important;
}
</style>
